import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { Text } from '@avanta/shared/components/Text';
import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useFetchPatient } from '@avanta/shared/hooks/useFetchPatient';
import { ChevronLeft, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { generatePath, Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { ROUTES } from 'constants/routes';
import { useFetchNextAppointments } from 'hooks/useFetchNextAppointments';
import { AppointmentCard } from './AppointmentCard';

export const AppointmentDetail = (): React.ReactElement => {
  const params = useParams();
  const patientId = params?.id;
  const { state } = useLocation();
  const { isLoading: isLoadingAppointments } = useFetchNextAppointments(patientId ?? '');
  const { data: patient, isLoading: isLoadingPatient } = useFetchPatient(patientId);
  const navigate = useNavigate();
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);

  if (!patientId) return <Navigate to={ROUTES.PATIENTS} />;
  if (isLoadingAppointments || isLoadingPatient) return <LoadingScreen />;

  const handleOnViewProfile = (): void => {
    navigate(generatePath(ROUTES.PATIENT_PROFILE, { id: patientId }));
  };

  const handleOnBack = (): void => {
    navigate(state?.from ?? ROUTES.PATIENTS);
  };

  return (
    <div className="flex flex-col gap-8">
      <Button
        variant="ghost"
        className="w-fit p-0 gap-2 hover:bg-transparent hover:text-teal-500 text-teal-600 -ml-1"
        onClick={handleOnBack}
      >
        <ChevronLeft className="size-5" />
        <Text variant="text-sm-medium" className="text-teal-600 hover:text-teal-500">
          {t('Common.back', { ns: LOCIZE_NS.COMMON })}
        </Text>
      </Button>
      <Text variant="heading-xl-medium">{t('InsuredPersonsList.manageAppointment.cta')}</Text>
      <AppointmentCard patientId={patientId} />
      <Card className="am-md:px-4 am-md:py-2 am-3xl:rounded-2xl">
        <div className="flex justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-1">
              <Text variant="text-sm-semibold">{t('AppointmentDetail.patient.label')}</Text>
              <Text variant="text-sm-regular">
                {patient?.firstName} {patient?.surname}
              </Text>
            </div>
            <div className="flex items-center gap-1">
              <Text variant="text-sm-semibold">{t('InsuredPersonsList.Table.nif.label')}</Text>
              <Text variant="text-sm-regular">{patient?.nif}</Text>
            </div>
            <div className="flex items-center gap-1">
              <Text variant="text-sm-semibold">{t('AppointmentDetail.email.label')}</Text>
              <Text variant="text-sm-regular">{patient?.email}</Text>
            </div>
          </div>
          <Button variant="terciary" size="md" icon="leading" onClick={handleOnViewProfile}>
            <User className="size-5" />
            {t('InsuredPersonsList.viewPerson.cta')}
          </Button>
        </div>
      </Card>
    </div>
  );
};
